import { type FC, useCallback, useMemo } from "react";
import notification from "@app/services/notification";
import { useScannerRegistration, type ScannerHintConfig, type ScannerRegistrationConfig } from "@app/scanner";
import type { StockActInvContent } from "../../../../../../api/stockActInvs";
import { resolveStockFullInventoryLotScan } from "../../helpers/resolveStockFullInventoryLotScan";

const FULL_INVENTORY_SCAN_HINT: ScannerHintConfig = {
    stateKey: "stock-full-inventory.card.scan-lot",
    title: "Сканируйте этикетку партии ТМЦ",
    description: "Найдём строку АИ по партии и откроем форму инвентаризации.",
    position: { placement: "bottom-left" },
    indicator: { variant: "scanner" },
};

/** Пропсы компонента {@link StockFullInventoryScanPlaceholder}. */
interface StockFullInventoryScanPlaceholderProps {
    /** Строки состава текущего АИ. */
    contents: StockActInvContent[];
    /** Включён ли сценарий сканирования партии. */
    enabled: boolean;
    /**
     * Открывает форму инвентаризации строки АИ.
     * @param contentCode код найденной строки АИ
     */
    onOpenContent: (contentCode: number) => void;
}

/** Регистрирует сценарий сканирования партии в карточке полной инвентаризации склада. */
export const StockFullInventoryScanPlaceholder: FC<StockFullInventoryScanPlaceholderProps> = ({
    contents,
    enabled,
    onOpenContent,
}) => {
    const handleScan = useCallback(
        async (barcode: string): Promise<void> => {
            const result = await resolveStockFullInventoryLotScan(barcode, contents);

            if (result.type === "notFound") {
                notification.warning(`Партия для скана "${barcode}" не найдена.`);
                return;
            }

            if (result.type === "contentNotFound") {
                notification.warning("Просканированная партия не относится к выбранному АИ.");
                return;
            }

            if (result.type === "ambiguous") {
                notification.warning("По партии найдено несколько строк АИ. Выберите строку вручную.");
                return;
            }

            onOpenContent(result.content.Code);
        },
        [contents, onOpenContent],
    );

    const scannerRegistration = useMemo(
        (): ScannerRegistrationConfig => ({
            enabled,
            priority: "screen",
            captureInEditable: false,
            hint: FULL_INVENTORY_SCAN_HINT,
            onScan: handleScan,
            errorSource: "stock.fullInventory.card.scanLot",
        }),
        [enabled, handleScan],
    );

    useScannerRegistration(scannerRegistration);

    return null;
};
